/**
 * Tatbikat Planner ve Dashboard metinleri (ADR 0128 Faz 2 Ek). Ortağın belgesindeki adlar
 * İngilizcede aynen (Drill, Frequency, Last Conducted, Next Due, Responsible Officer,
 * Participants, Postponement, Status). Hata anahtarları sunucunun kodlarıyla eşleşir.
 */

export const drillPlanEn = {
  months_one: '{{count}} month',
  months_other: '{{count}} months',
  hours_one: '{{count}} hour',
  hours_other: '{{count}} hours',
  days_one: '{{count}} day',
  days_other: '{{count}} days',
  onBehalf: 'on behalf of Master',
  onBehalfNote:
    'You are deciding on behalf of the Master (ADR 0127). The record shows your name with “on behalf of Master”.',
  originalShort: 'original {{date}}',
  status: {
    draft: 'Draft',
    planned: 'Planned',
    due: 'Due',
    in_progress: 'In Progress',
    pending_review: 'Pending Review',
    completed: 'Completed',
    overdue: 'Overdue',
    postponed: 'Postponed',
    cancelled: 'Cancelled',
  },
  review: {
    pending: 'Pending',
    approved: 'Approved',
    returned: 'Returned',
    in_effect: 'In Effect',
    ended: 'Ended',
    replaced: 'Replaced',
    draft: 'Draft',
  },
  state: { draft: 'Draft', active: 'Active', cancelled: 'Cancelled' },
  basis: {
    start: 'from start date',
    interval: 'from last conducted',
    postponed: 'postponed',
    crew_change: '25% crew change (24 h)',
  },
  intervalSource: { plan: 'vessel plan', rule: 'requirement', template: 'template' },
  conductKind: {
    baseline: 'Baseline entry',
    recorded: 'Recorded drill',
    executed: 'Drill record',
  },
  marks: { done: 'Conducted', due: 'Due', postponed: 'Postponed', overdue: 'Overdue' },
  actions: {
    newPlan: 'Add Drill to Plan',
    save: 'Save',
    cancel: 'Cancel',
    back: 'Close',
    edit: 'Edit Plan',
    recordConduct: 'Enter Past Drill',
    conductDrill: 'Conduct Drill',
    continueDrill: 'Continue Drill',
    postpone: 'Request Postponement',
    variant: 'Vessel Scenario',
    cancelPlan: 'Remove from Plan',
  },
  filters: {
    vessel: 'Vessel',
    allVessels: 'All vessels',
    status: 'Status',
    allStatuses: 'All statuses',
    type: 'Drill Type',
    allTypes: 'All types',
    responsible: 'Responsible Officer',
    anyone: 'Anyone',
    from: 'From',
    to: 'To',
    clear: 'Clear',
  },
  columns: {
    vessel: 'Vessel',
    drill: 'Drill',
    frequency: 'Frequency',
    lastConducted: 'Last Conducted',
    nextDue: 'Next Due',
    responsible: 'Responsible Officer',
    participants: 'Participants',
    postponement: 'Postponement',
    status: 'Status',
    date: 'Date',
    event: 'Event',
  },
  planner: {
    description:
      'The vessel drill plan: next due dates follow the requirement (SOLAS, ISPS or company SMS) from the last approved drill; postponements and recorded drills are approved by the Master.',
    officeDescription:
      'Fleet drill plans as reported by the vessels. Approve vessel-specific scenarios and maintain the company rule set here.',
    error: 'The drill plan could not be loaded.',
    previous: 'Previous',
    next: 'Next',
    wholeMonth: 'Whole month',
    tabs: {
      plans: 'Plans',
      annual: 'Annual',
      monthly: 'Monthly',
      calendar: 'Calendar',
      postponements: 'Postponements',
      variants: 'Vessel Scenarios',
      rules: 'Rule Set',
    },
    empty: {
      noPlans: 'No drills are planned yet.',
      noMatch: 'No drills match the filters.',
      noEvents: 'No drills in this period.',
      noPostponements: 'No postponements.',
      noVariants: 'No vessel-specific scenarios.',
    },
  },
  plan: {
    newTitle: 'Add Drill to Plan',
    editTitle: 'Edit Drill Plan',
    template: 'Drill',
    noTemplates: 'No active drill templates left to plan.',
    startOn: 'Plan Start Date',
    startOnHint: 'The first due date when the drill has no approved record yet.',
    responsible: 'Responsible Officer',
    participants: 'Participants (roles)',
    participantsHint: 'Press Enter after each role.',
    interval: 'Vessel Interval (months)',
    intervalHint: 'Optional. Shorter than the requirement only; empty follows the requirement.',
    notes: 'Notes',
    draft: 'Save as draft',
    draftHint: 'A draft has no due date and does not appear on the dashboard.',
  },
  detail: {
    awaitingDecision: 'Awaiting the Master’s decision',
    decideConduct: 'Decide drill of {{date}}',
    decideRecord: 'Open {{no}} ({{date}}) to decide',
    decidePostponement: 'Decide postponement to {{date}}',
    vessel: 'Vessel',
    category: 'Drill Type',
    frequency: 'Frequency',
    rule: 'Requirement',
    lastConducted: 'Last Conducted',
    nextDue: 'Next Due',
    originalDue: 'Original Due Date',
    crewChange: 'Crew Change',
    crewChangeValue:
      '{{newcomers}} of {{onBoard}} joined on {{date}}: drill due by {{due}} (SOLAS III/19.2.2)',
    responsible: 'Responsible Officer',
    participants: 'Participants',
    startOn: 'Plan Start Date',
    state: 'Plan',
    cancelled: 'Removed from plan',
    conducts: 'Recorded Drills',
    noConducts: 'No drill has been recorded yet.',
    date: 'Date',
    kind: 'Type',
    reference: 'Reference / Notes',
    enteredBy: 'Entered By',
    decision: 'Master’s Decision',
    postponements: 'Postponements',
    noPostponements: 'No postponements.',
  },
  postponements: {
    vessel: 'Vessel',
    drill: 'Drill',
    reason: 'Reason',
    requestedBy: 'Requested By',
    decidedBy: 'Decided By',
    originalDue: 'Original Due',
    newDue: 'New Due',
    delay: 'Delay',
    status: 'Status',
  },
  conduct: {
    title: 'Record Drill',
    kind: 'Record type',
    recordedHint:
      'A drill conducted on board. It becomes the Last Conducted date once the Master approves it.',
    baselineHint:
      'The last drill held before Felix was used, entered once to start the plan. A reason is required; the Master approves it.',
    date: 'Date Conducted',
    reference: 'Reference (log book page, form no.)',
    notes: 'Notes',
    reason: 'Reason / Source',
    submit: 'Send for Approval',
  },
  postpone: {
    title: 'Postpone {{code}}',
    drill: 'Drill',
    currentDue: 'Current Due Date',
    originalDue: 'Original Due Date',
    latest: 'Latest Allowed',
    newDue: 'New Due Date',
    reason: 'Reason',
    note: 'The Master approves the request; the due date changes only after approval. At most one interval later than the original due date.',
    submit: 'Send Request',
  },
  decision: {
    conductTitle: 'Approve Recorded Drill',
    postponementTitle: 'Approve Postponement',
    drill: 'Drill',
    date: 'Date Conducted',
    kind: 'Type',
    enteredBy: 'Entered By',
    reference: 'Reference',
    notes: 'Notes',
    originalDue: 'Original Due Date',
    currentDue: 'Current Due Date',
    newDue: 'New Due Date',
    requestedBy: 'Requested By',
    reason: 'Reason',
    comment: 'Comment',
    commentHint: 'Required when returning.',
    password: 'Password',
    approve: 'Approve',
    return: 'Return',
  },
  cancelPlan: {
    title: 'Remove {{code}} from Plan',
    note: 'The plan and its records are kept; the drill no longer has a due date. This cannot be undone; add the drill again if needed.',
    reason: 'Reason',
    submit: 'Remove',
  },
  variant: {
    title: 'Vessel Scenario',
    note: 'Adapts the scenario, steps and equipment to this vessel. The central template does not change; the office approves the vessel scenario.',
    linesHint: 'One item per line.',
    reason: 'Reason for the vessel scenario',
    saveDraft: 'Save Draft',
    submit: 'Send to Office',
    decisionTitle: 'Vessel Scenario',
    vessel: 'Vessel',
    baseVersion: 'Template Version',
    by: 'Prepared By',
    central: 'Central Template',
    proposed: 'Vessel Scenario',
    updated: 'Last Change',
    status: 'Status',
    actions: 'Actions',
    review: 'Review',
    view: 'View',
    edit: 'Edit vessel scenario',
    newerTemplate: 'Template now v{{version}}',
  },
  rules: {
    note: 'Default requirements from SOLAS, ISPS and good practice. A company rule may apply to vessel types or flags; SOLAS and ISPS intervals can only be made stricter.',
    add: 'Add Company Rule',
    newTitle: 'Add Company Rule',
    editTitle: 'Edit Company Rule',
    company: 'Company Rules',
    defaults: 'Default Requirements',
    noCompanyRules: 'No company rules; the defaults apply to every vessel.',
    code: 'Requirement',
    title: 'Title',
    reference: 'Reference',
    condition: 'Applies To',
    allVessels: 'All vessels',
    value: 'Interval',
    reason: 'Reason',
    reasonHint: 'Kept in the audit trail with your name.',
    updatedBy: 'Last Changed',
    status: 'Status',
    edit: 'Edit rule',
    notApplicable: 'Not applicable',
    passengerOnly: 'passenger ships',
    basisLabel: 'Basis',
    vesselTypes: 'Vessel Types',
    vesselTypesHint: 'Empty applies to all types. Press Enter after each type.',
    flags: 'Flags',
    flagsHint: 'Empty applies to all flags. Press Enter after each flag.',
    applicable: 'Applies',
    applicableHint: 'Turn off to mark the requirement as not applicable (exemption) with a reason.',
    intervalMonths: 'Interval (months)',
    triggerHours: 'Within (hours)',
    active: 'Active',
    basis: { regulation: 'Regulation', company_sms: 'Company SMS' },
  },
  dashboard: {
    description:
      'Drill compliance for the period: planned, completed, upcoming and overdue drills on this vessel.',
    officeDescription:
      'Drill compliance across the fleet for the period; choose a vessel to see its own figures.',
    error: 'The drill dashboard could not be loaded.',
    period: 'Period',
    periods: { month: 'This Month', quarter: 'This Quarter', year: 'This Year' },
    openPlanner: '{{label}}: open the planner',
    kpi: {
      planned: 'Planned',
      completed: 'Completed',
      upcoming: 'Upcoming (30 days)',
      overdue: 'Overdue',
      rate: 'Completion Rate',
    },
    rateNote:
      'Completion rate: {{onTime}} of {{counted}} due drills held on time. {{postponed}} postponed.',
    attention: 'Overdue and Upcoming',
    noAttention: 'Nothing overdue or due in the next 30 days.',
    recent: 'Recently Completed',
    noRecent: 'No approved drills yet.',
    fleet: 'Fleet Comparison',
  },
  fieldErrors: {
    REQUIRED: 'Required.',
    INVALID: 'Check this field.',
    INVALID_DATE: 'Enter a valid date.',
    INVALID_NUMBER: 'Enter a whole number.',
    INVALID_OPTION: 'Choose an option from the list.',
    INVALID_ID: 'Choose an option from the list.',
    INVALID_TEXT: 'Enter text.',
    INVALID_LIST: 'Check the list.',
    INVALID_BOOLEAN: 'Check this field.',
    OUT_OF_RANGE: 'The value is out of range.',
    TOO_LONG: 'The text is too long.',
    TOO_MANY: 'Too many items.',
    FUTURE_DATE: 'The date cannot be in the future.',
    DUE_IN_PAST: 'The new due date cannot be in the past.',
    POSTPONEMENT_MUST_BE_LATER: 'The new due date must be later than the current one.',
    POSTPONEMENT_TOO_LONG: 'At most one interval later than the original due date.',
    LOOSER_THAN_REGULATION: 'A SOLAS or ISPS interval can only be made stricter.',
    INTERVAL_EXCEEDS_RULE: 'The vessel interval cannot be longer than the requirement.',
  },
  errors: {
    GENERIC: 'The operation failed. Try again.',
    FORBIDDEN: 'You do not have permission for this.',
    NOT_FOUND: 'The record no longer exists. Refresh the page.',
    MASTER_ONLY: 'Only the Master or the acting Master can decide.',
    ALREADY_DECIDED: 'This has already been decided. Refresh the page.',
    CONTENT_HASH_MISMATCH: 'The record changed after you opened it. Refresh and review again.',
    STALE_CONTENT: 'Someone else changed this record. Refresh the page.',
    STALE_DUE_DATE: 'The due date changed meanwhile. Refresh the page.',
    DUPLICATE_DRILL_PLAN: 'This drill is already in the vessel plan.',
    DRILL_PLAN_CANCELLED: 'This drill was removed from the plan.',
    DRILL_PLAN_NOT_ACTIVE: 'Only an active plan can be postponed.',
    DRILL_PLAN_TEMPLATE_FIXED: 'The drill of a plan cannot be changed.',
    DRILL_TEMPLATE_INACTIVE: 'The drill template is inactive.',
    CONDUCT_PENDING: 'A recorded drill is already awaiting the Master’s decision.',
    DUPLICATE_CONDUCT_DATE: 'A drill is already recorded for this date.',
    POSTPONEMENT_PENDING: 'A postponement is already awaiting the Master’s decision.',
    POSTPONEMENT_MUST_BE_LATER: 'The new due date must be later than the current one.',
    POSTPONEMENT_TOO_LONG: 'At most one interval later than the original due date.',
    DUE_IN_PAST: 'The new due date cannot be in the past.',
    FUTURE_DATE: 'The date cannot be in the future.',
    INTERVAL_EXCEEDS_RULE: 'The vessel interval cannot be longer than the requirement.',
    LOOSER_THAN_REGULATION: 'A SOLAS or ISPS interval can only be made stricter.',
    VESSEL_MISMATCH: 'The record belongs to another vessel.',
    REAUTH_REQUIRED: 'Enter your password to confirm.',
    REAUTH_FAILED: 'The password is not correct.',
    VALIDATION_FAILED: 'Check the fields.',
  },
};

export const drillPlanTr: typeof drillPlanEn = {
  months_one: '{{count}} ay',
  months_other: '{{count}} ay',
  hours_one: '{{count}} saat',
  hours_other: '{{count}} saat',
  days_one: '{{count}} gün',
  days_other: '{{count}} gün',
  onBehalf: 'kaptan adına',
  onBehalfNote:
    'Kaptan adına karar veriyorsunuz (ADR 0127). Kayıtta adınız “kaptan adına” notuyla görünür.',
  originalShort: 'ilk vade {{date}}',
  status: {
    draft: 'Taslak',
    planned: 'Planlandı',
    due: 'Vadesi Geldi',
    in_progress: 'Sürüyor',
    pending_review: 'Onay Bekliyor',
    completed: 'Tamamlandı',
    overdue: 'Gecikmiş',
    postponed: 'Ertelendi',
    cancelled: 'İptal',
  },
  review: {
    pending: 'Bekliyor',
    approved: 'Onaylandı',
    returned: 'İade Edildi',
    in_effect: 'Yürürlükte',
    ended: 'Sona Erdi',
    replaced: 'Yenisiyle Değişti',
    draft: 'Taslak',
  },
  state: { draft: 'Taslak', active: 'Etkin', cancelled: 'İptal' },
  basis: {
    start: 'başlangıç tarihinden',
    interval: 'son uygulamadan',
    postponed: 'ertelendi',
    crew_change: '%25 mürettebat değişimi (24 saat)',
  },
  intervalSource: { plan: 'gemi planı', rule: 'gereklilik', template: 'şablon' },
  conductKind: {
    baseline: 'Başlangıç kaydı',
    recorded: 'Kayıtlı tatbikat',
    executed: 'Tatbikat kaydı',
  },
  marks: { done: 'Yapıldı', due: 'Vade', postponed: 'Ertelendi', overdue: 'Gecikmiş' },
  actions: {
    newPlan: 'Plana Tatbikat Ekle',
    save: 'Kaydet',
    cancel: 'Vazgeç',
    back: 'Kapat',
    edit: 'Planı Düzenle',
    recordConduct: 'Geçmiş Tatbikatı Gir',
    conductDrill: 'Tatbikatı Başlat',
    continueDrill: 'Tatbikata Devam Et',
    postpone: 'Erteleme İste',
    variant: 'Gemi Senaryosu',
    cancelPlan: 'Plandan Çıkar',
  },
  filters: {
    vessel: 'Gemi',
    allVessels: 'Bütün gemiler',
    status: 'Durum',
    allStatuses: 'Bütün durumlar',
    type: 'Tatbikat Türü',
    allTypes: 'Bütün türler',
    responsible: 'Sorumlu Zabit',
    anyone: 'Herkes',
    from: 'Başlangıç',
    to: 'Bitiş',
    clear: 'Temizle',
  },
  columns: {
    vessel: 'Gemi',
    drill: 'Tatbikat',
    frequency: 'Sıklık',
    lastConducted: 'Son Uygulama',
    nextDue: 'Sonraki Vade',
    responsible: 'Sorumlu Zabit',
    participants: 'Katılımcılar',
    postponement: 'Erteleme',
    status: 'Durum',
    date: 'Tarih',
    event: 'Olay',
  },
  planner: {
    description:
      'Geminin tatbikat planı: sonraki vade, son onaylı tatbikattan gerekliliğe (SOLAS, ISPS ya da şirket SMS) göre hesaplanır; erteleme ve kayıtlı tatbikatı kaptan onaylar.',
    officeDescription:
      'Gemilerin bildirdiği filo tatbikat planları. Gemiye özel senaryoları burada onaylayın, şirket kural setini burada yönetin.',
    error: 'Tatbikat planı yüklenemedi.',
    previous: 'Önceki',
    next: 'Sonraki',
    wholeMonth: 'Bütün ay',
    tabs: {
      plans: 'Planlar',
      annual: 'Yıllık',
      monthly: 'Aylık',
      calendar: 'Takvim',
      postponements: 'Ertelemeler',
      variants: 'Gemi Senaryoları',
      rules: 'Kural Seti',
    },
    empty: {
      noPlans: 'Henüz planlı tatbikat yok.',
      noMatch: 'Süzgeçlere uyan tatbikat yok.',
      noEvents: 'Bu dönemde tatbikat yok.',
      noPostponements: 'Erteleme yok.',
      noVariants: 'Gemiye özel senaryo yok.',
    },
  },
  plan: {
    newTitle: 'Plana Tatbikat Ekle',
    editTitle: 'Tatbikat Planını Düzenle',
    template: 'Tatbikat',
    noTemplates: 'Plana eklenecek etkin tatbikat şablonu kalmadı.',
    startOn: 'Plan Başlangıç Tarihi',
    startOnHint: 'Tatbikatın onaylı kaydı yokken ilk vade.',
    responsible: 'Sorumlu Zabit',
    participants: 'Katılımcılar (roller)',
    participantsHint: 'Her rolden sonra Enter’a basın.',
    interval: 'Gemi Aralığı (ay)',
    intervalHint: 'İsteğe bağlı. Yalnız gereklilikten kısa olabilir; boşsa gereklilik geçerlidir.',
    notes: 'Notlar',
    draft: 'Taslak olarak kaydet',
    draftHint: 'Taslağın vadesi yoktur, panoda görünmez.',
  },
  detail: {
    awaitingDecision: 'Kaptanın kararını bekliyor',
    decideConduct: '{{date}} tatbikatına karar ver',
    decideRecord: 'Karar için {{no}} kaydını aç ({{date}})',
    decidePostponement: '{{date}} ertelemesine karar ver',
    vessel: 'Gemi',
    category: 'Tatbikat Türü',
    frequency: 'Sıklık',
    rule: 'Gereklilik',
    lastConducted: 'Son Uygulama',
    nextDue: 'Sonraki Vade',
    originalDue: 'İlk Vade',
    crewChange: 'Mürettebat Değişimi',
    crewChangeValue:
      '{{date}} tarihinde {{onBoard}} kişiden {{newcomers}} kişi katıldı: tatbikat en geç {{due}} (SOLAS III/19.2.2)',
    responsible: 'Sorumlu Zabit',
    participants: 'Katılımcılar',
    startOn: 'Plan Başlangıç Tarihi',
    state: 'Plan',
    cancelled: 'Plandan çıkarıldı',
    conducts: 'Kayıtlı Tatbikatlar',
    noConducts: 'Henüz tatbikat kaydedilmedi.',
    date: 'Tarih',
    kind: 'Tür',
    reference: 'Referans / Not',
    enteredBy: 'Giren',
    decision: 'Kaptanın Kararı',
    postponements: 'Ertelemeler',
    noPostponements: 'Erteleme yok.',
  },
  postponements: {
    vessel: 'Gemi',
    drill: 'Tatbikat',
    reason: 'Gerekçe',
    requestedBy: 'İsteyen',
    decidedBy: 'Karar Veren',
    originalDue: 'İlk Vade',
    newDue: 'Yeni Vade',
    delay: 'Gecikme',
    status: 'Durum',
  },
  conduct: {
    title: 'Tatbikatı Kaydet',
    kind: 'Kayıt türü',
    recordedHint: 'Gemide yapılan tatbikat. Kaptan onaylayınca Son Uygulama tarihi olur.',
    baselineHint:
      'Felix’ten önce yapılan son tatbikat; planı başlatmak için bir kez girilir. Gerekçe zorunludur; kaptan onaylar.',
    date: 'Uygulama Tarihi',
    reference: 'Referans (jurnal sayfası, form no.)',
    notes: 'Notlar',
    reason: 'Gerekçe / Kaynak',
    submit: 'Onaya Gönder',
  },
  postpone: {
    title: '{{code}} Ertele',
    drill: 'Tatbikat',
    currentDue: 'Güncel Vade',
    originalDue: 'İlk Vade',
    latest: 'En Geç',
    newDue: 'Yeni Vade',
    reason: 'Gerekçe',
    note: 'Talebi kaptan onaylar; vade yalnız onaydan sonra değişir. İlk vadeden en çok bir aralık sonrası seçilebilir.',
    submit: 'Talebi Gönder',
  },
  decision: {
    conductTitle: 'Kayıtlı Tatbikatı Onayla',
    postponementTitle: 'Ertelemeyi Onayla',
    drill: 'Tatbikat',
    date: 'Uygulama Tarihi',
    kind: 'Tür',
    enteredBy: 'Giren',
    reference: 'Referans',
    notes: 'Notlar',
    originalDue: 'İlk Vade',
    currentDue: 'Güncel Vade',
    newDue: 'Yeni Vade',
    requestedBy: 'İsteyen',
    reason: 'Gerekçe',
    comment: 'Yorum',
    commentHint: 'İadede zorunlu.',
    password: 'Parola',
    approve: 'Onayla',
    return: 'İade Et',
  },
  cancelPlan: {
    title: '{{code}} Plandan Çıkar',
    note: 'Plan ve kayıtları saklanır; tatbikatın vadesi kalmaz. Geri alınmaz; gerekirse tatbikat yeniden eklenir.',
    reason: 'Gerekçe',
    submit: 'Plandan Çıkar',
  },
  variant: {
    title: 'Gemi Senaryosu',
    note: 'Senaryoyu, adımları ve ekipmanı bu gemiye uyarlar. Merkez şablon değişmez; gemi senaryosunu ofis onaylar.',
    linesHint: 'Her satıra bir madde.',
    reason: 'Gemi senaryosunun gerekçesi',
    saveDraft: 'Taslağı Kaydet',
    submit: 'Ofise Gönder',
    decisionTitle: 'Gemi Senaryosu',
    vessel: 'Gemi',
    baseVersion: 'Şablon Sürümü',
    by: 'Hazırlayan',
    central: 'Merkez Şablon',
    proposed: 'Gemi Senaryosu',
    updated: 'Son Değişiklik',
    status: 'Durum',
    actions: 'İşlemler',
    review: 'İncele',
    view: 'Görüntüle',
    edit: 'Gemi senaryosunu düzenle',
    newerTemplate: 'Şablon artık v{{version}}',
  },
  rules: {
    note: 'SOLAS, ISPS ve iyi uygulamadan varsayılan gereklilikler. Şirket kuralı gemi tipine ya da bayrağa göre uygulanabilir; SOLAS ve ISPS aralıkları yalnız sıkılaştırılabilir.',
    add: 'Şirket Kuralı Ekle',
    newTitle: 'Şirket Kuralı Ekle',
    editTitle: 'Şirket Kuralını Düzenle',
    company: 'Şirket Kuralları',
    defaults: 'Varsayılan Gereklilikler',
    noCompanyRules: 'Şirket kuralı yok; varsayılanlar bütün gemilerde geçerli.',
    code: 'Gereklilik',
    title: 'Başlık',
    reference: 'Dayanak',
    condition: 'Uygulandığı Gemiler',
    allVessels: 'Bütün gemiler',
    value: 'Aralık',
    reason: 'Gerekçe',
    reasonHint: 'Adınızla denetim izinde saklanır.',
    updatedBy: 'Son Değiştiren',
    status: 'Durum',
    edit: 'Kuralı düzenle',
    notApplicable: 'Uygulanmaz',
    passengerOnly: 'yolcu gemileri',
    basisLabel: 'Dayanak Türü',
    vesselTypes: 'Gemi Tipleri',
    vesselTypesHint: 'Boşsa bütün tiplere uygulanır. Her tipten sonra Enter’a basın.',
    flags: 'Bayraklar',
    flagsHint: 'Boşsa bütün bayraklara uygulanır. Her bayraktan sonra Enter’a basın.',
    applicable: 'Uygulanır',
    applicableHint: 'Kapatılırsa gereklilik gerekçeyle “uygulanmaz” (muafiyet) olur.',
    intervalMonths: 'Aralık (ay)',
    triggerHours: 'Süre (saat)',
    active: 'Etkin',
    basis: { regulation: 'Mevzuat', company_sms: 'Şirket SMS' },
  },
  dashboard: {
    description:
      'Dönemin tatbikat uyumu: bu gemide planlı, tamamlanan, yaklaşan ve geciken tatbikatlar.',
    officeDescription: 'Filonun dönem tatbikat uyumu; geminin kendi rakamları için gemi seçin.',
    error: 'Tatbikat panosu yüklenemedi.',
    period: 'Dönem',
    periods: { month: 'Bu Ay', quarter: 'Bu Çeyrek', year: 'Bu Yıl' },
    openPlanner: '{{label}}: Planner’ı aç',
    kpi: {
      planned: 'Planlı',
      completed: 'Tamamlanan',
      upcoming: 'Yaklaşan (30 gün)',
      overdue: 'Gecikmiş',
      rate: 'Tamamlanma Oranı',
    },
    rateNote:
      'Tamamlanma oranı: vadesi gelen {{counted}} tatbikattan {{onTime}} tanesi zamanında yapıldı. {{postponed}} erteleme.',
    attention: 'Geciken ve Yaklaşan',
    noAttention: 'Geciken ya da 30 gün içinde vadesi gelen tatbikat yok.',
    recent: 'Son Tamamlananlar',
    noRecent: 'Henüz onaylı tatbikat yok.',
    fleet: 'Filo Karşılaştırması',
  },
  fieldErrors: {
    REQUIRED: 'Zorunlu.',
    INVALID: 'Bu alanı kontrol edin.',
    INVALID_DATE: 'Geçerli bir tarih girin.',
    INVALID_NUMBER: 'Tam sayı girin.',
    INVALID_OPTION: 'Listeden seçin.',
    INVALID_ID: 'Listeden seçin.',
    INVALID_TEXT: 'Metin girin.',
    INVALID_LIST: 'Listeyi kontrol edin.',
    INVALID_BOOLEAN: 'Bu alanı kontrol edin.',
    OUT_OF_RANGE: 'Değer aralık dışında.',
    TOO_LONG: 'Metin çok uzun.',
    TOO_MANY: 'Çok fazla madde.',
    FUTURE_DATE: 'Tarih gelecekte olamaz.',
    DUE_IN_PAST: 'Yeni vade geçmişte olamaz.',
    POSTPONEMENT_MUST_BE_LATER: 'Yeni vade güncel vadeden sonra olmalı.',
    POSTPONEMENT_TOO_LONG: 'İlk vadeden en çok bir aralık sonrası seçilebilir.',
    LOOSER_THAN_REGULATION: 'SOLAS ya da ISPS aralığı yalnız sıkılaştırılabilir.',
    INTERVAL_EXCEEDS_RULE: 'Gemi aralığı gereklilikten uzun olamaz.',
  },
  errors: {
    GENERIC: 'İşlem yapılamadı. Yeniden deneyin.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Kayıt artık yok. Sayfayı yenileyin.',
    MASTER_ONLY: 'Yalnız kaptan ya da kaptan vekili karar verebilir.',
    ALREADY_DECIDED: 'Bu konuda karar verilmiş. Sayfayı yenileyin.',
    CONTENT_HASH_MISMATCH: 'Kayıt siz açtıktan sonra değişti. Yenileyip yeniden inceleyin.',
    STALE_CONTENT: 'Kaydı başka biri değiştirdi. Sayfayı yenileyin.',
    STALE_DUE_DATE: 'Vade bu arada değişti. Sayfayı yenileyin.',
    DUPLICATE_DRILL_PLAN: 'Bu tatbikat gemi planında zaten var.',
    DRILL_PLAN_CANCELLED: 'Bu tatbikat plandan çıkarılmış.',
    DRILL_PLAN_NOT_ACTIVE: 'Yalnız etkin plan ertelenebilir.',
    DRILL_PLAN_TEMPLATE_FIXED: 'Planın tatbikatı değiştirilemez.',
    DRILL_TEMPLATE_INACTIVE: 'Tatbikat şablonu pasif.',
    CONDUCT_PENDING: 'Kaptanın kararını bekleyen kayıtlı tatbikat zaten var.',
    DUPLICATE_CONDUCT_DATE: 'Bu tarih için tatbikat zaten kayıtlı.',
    POSTPONEMENT_PENDING: 'Kaptanın kararını bekleyen erteleme zaten var.',
    POSTPONEMENT_MUST_BE_LATER: 'Yeni vade güncel vadeden sonra olmalı.',
    POSTPONEMENT_TOO_LONG: 'İlk vadeden en çok bir aralık sonrası seçilebilir.',
    DUE_IN_PAST: 'Yeni vade geçmişte olamaz.',
    FUTURE_DATE: 'Tarih gelecekte olamaz.',
    INTERVAL_EXCEEDS_RULE: 'Gemi aralığı gereklilikten uzun olamaz.',
    LOOSER_THAN_REGULATION: 'SOLAS ya da ISPS aralığı yalnız sıkılaştırılabilir.',
    VESSEL_MISMATCH: 'Kayıt başka bir gemiye ait.',
    REAUTH_REQUIRED: 'Onaylamak için parolanızı girin.',
    REAUTH_FAILED: 'Parola doğru değil.',
    VALIDATION_FAILED: 'Alanları kontrol edin.',
  },
};
